// First-touch UTM + referrer capture. Persists the very first visit's
// UTM parameters and referrer in localStorage so signup/first-booking
// events can be attributed to the original campaign.

import { getActivePlatform } from "@/lib/platform/config";
import { getConsent, hasChosen } from "./consent";
import { normalizeAcquisition } from "@workspace/booking-rules";
const UTM_KEY = "bac_first_touch_v1";
let bacFirstTouch: FirstTouch | null = null;
function permitted(): boolean { return getActivePlatform() !== "bac" || (hasChosen() && getConsent().analytics); }
const COOKIE_MAX_AGE = 60 * 60 * 24 * 180; // 180 days

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find((row) => row.startsWith(name + "="));
  if (!match) return null;
  try { return decodeURIComponent(match.slice(name.length + 1)); } catch { return null; }
}

function writeCookie(name: string, value: string) {
  if (typeof document === "undefined") return;
  const secure = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
}
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
  "msclkid",
] as const;

export type FirstTouch = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  referrer?: string;
  landing_path?: string;
  ts: number;
};

export function captureFirstTouch(): FirstTouch {
  if (typeof window === "undefined") return { ts: 0 };
  if (!permitted()) {
    bacFirstTouch = null;
    // Delete the overlapping unsigned legacy store; never import it as evidence.
    try { localStorage.removeItem(UTM_KEY); } catch {}
    document.cookie = `${UTM_KEY}=; Max-Age=0; Path=/; SameSite=Lax`;
    return { ts: 0 };
  }
  if (getActivePlatform() === "bac") {
    if (bacFirstTouch) return bacFirstTouch;
    const normalized = normalizeAcquisition({ surface: "web", url: window.location.href, referrer: document.referrer }, getConsent().marketing);
    if (!normalized) return { ts: 0 };
    // BAC's consented SDK properties are minimised, ephemeral, and independent
    // of the signed server-side acquisition store. Never trust an old cookie.
    bacFirstTouch = { ts: Date.now(), ...Object.fromEntries(Object.entries(normalized).filter(([,value]) => typeof value === "string")) } as FirstTouch;
    return bacFirstTouch;
  }
  // Read from localStorage first, fall back to cookie (covers ITP/private modes
  // where localStorage may have been cleared but the cookie survives).
  try {
    const existing = localStorage.getItem(UTM_KEY) ?? readCookie(UTM_KEY);
    if (existing) {
      const parsed = JSON.parse(existing) as FirstTouch;
      // Mirror back to whichever store was empty so both are in sync.
      try { localStorage.setItem(UTM_KEY, existing); } catch {}
      writeCookie(UTM_KEY, existing);
      return parsed;
    }
  } catch {}

  const sp = new URLSearchParams(window.location.search);
  const touch: FirstTouch = { ts: Date.now() };
  for (const k of UTM_KEYS) {
    const v = sp.get(k);
    if (getActivePlatform() === "bac" && ["gclid","fbclid","msclkid"].includes(k) && !getConsent().marketing) continue;
    if (v) (touch as Record<string, unknown>)[k] = v;
  }
  const ref = document.referrer;
  if (ref && !ref.startsWith(window.location.origin)) touch.referrer = ref;
  touch.landing_path = window.location.pathname + window.location.search;

  const serialized = JSON.stringify(touch);
  try { localStorage.setItem(UTM_KEY, serialized); } catch {}
  writeCookie(UTM_KEY, serialized);
  return touch;
}

export function getFirstTouch(): FirstTouch | null {
  if (typeof window === "undefined") return null;
  if (getActivePlatform() === "bac") return permitted() ? bacFirstTouch : null;
  try {
    const raw = localStorage.getItem(UTM_KEY) ?? readCookie(UTM_KEY);
    return raw ? (JSON.parse(raw) as FirstTouch) : null;
  } catch {
    return null;
  }
}

export function getCurrentUtm(): Record<string, string> {
  if (typeof window === "undefined") return {};
  if (!permitted()) return {};
  const sp = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  for (const k of UTM_KEYS) {
    const v = sp.get(k);
    if (v) out[k] = v;
  }
  return out;
}
